import i18n from 'i18next';
import { configs, displayNameTypes, formats, imperialLocales, kilometersInMile, ptPT, unitTypes } from './constants';
import { createIntl, FormatDisplayNameOptions } from '@formatjs/intl';
import { DateInput, DateObjectInput, NumberWithConfigInput, TimeInput } from './types';
import { intl } from './intl';
import { selectUnit } from '@formatjs/intl-utils';
import { TFunction } from 'i18next';

// CLDR locale data for pt-PT defines dateStyle 'medium' as an all-numeric pattern (dd/MM/y),
// making it visually indistinguishable from 'short' (dd/MM/yy). Map to the base 'pt' locale
// which uses an abbreviated-month medium pattern (d 'de' MMM 'de' y).
const dateMediumLocaleOverrides: Record<string, string> = {
  [ptPT]: 'pt'
};

export const isImperialLocale = () => {
  return imperialLocales.includes(i18n.language);
};

export function convertMetersToKilometers(value: number): number {
  return value / 1000;
}

export function convertMilesToKilometers(value: number): number {
  return value * kilometersInMile;
}

export function convertKilometersToMiles(value: number): number {
  return value / kilometersInMile;
}

/**
 * Ensures a date value is interpreted as UTC.
 * ISO datetime strings without timezone info (no Z or ±HH:MM offset)
 * get 'Z' appended before parsing to force UTC interpretation.
 * Date-only strings are left as-is (JS spec already treats them as UTC).
 * Date objects pass through unchanged.
 */
export function ensureUTCDate(value: string | Date): Date {
  if (value instanceof Date) return value;
  if (typeof value === 'string' && value.length > 0) {
    const hasTimePart = /T\d{2}/.test(value);
    const hasTimezone = /Z|[+-]\d{2}(:\d{2})?$/i.test(value);
    if (hasTimePart && !hasTimezone) {
      return new Date(value + 'Z');
    }
  }
  return new Date(value);
}

export function extractDateInput(
  input: string | Date | DateObjectInput | null
): { value: string | Date | null; options?: { timeZone: string } } {
  if (input !== null && typeof input === 'object' && !(input instanceof Date) && input.timeZoneId) {
    return { value: input.value, options: { timeZone: input.timeZoneId } };
  }
  return { value: input as string | Date | null };
}

export function formatDate(input: DateInput, format: string): string {
  switch (format) {
    case formats.dateCustom: {
      const dateObjectInput = input as DateObjectInput;
      // NOTE: timezone override for dateCustom uses input.config.timeZone, not the
      // { value, timeZoneId } pattern used by dateFull/dateLong/dateMedium/dateShort.
      if (!dateObjectInput || typeof dateObjectInput !== 'object' || !dateObjectInput.config) {
        throw new Error('dateCustom format requires an input object with a config property');
      }
      // For some reason when 'day' displaying is not configured, the date format breaks
      if (dateObjectInput.config.year && dateObjectInput.config.month && !dateObjectInput.config.day) {
        const parts = intl.formatDateToParts(ensureUTCDate(dateObjectInput.value), {
          ...dateObjectInput.config,
          timeZone: dateObjectInput.timeZoneId,
          day: 'numeric'
        });
        let literalRemoved = false;
        return parts
          .filter((part, index) => {
            const isClosestDayLiteral =
              !literalRemoved &&
              ((part.type === 'literal' && parts[index + 1].type === 'day') ||
                (part.type === 'literal' && parts[index - 1].type === 'day'));
            if (isClosestDayLiteral) {
              literalRemoved = true;
            }
            return !(part.type === 'day' || isClosestDayLiteral);
          })
          .map(part => part.value)
          .join('');
      }
      return intl.formatDate(ensureUTCDate(dateObjectInput.value), {
        ...dateObjectInput.config,
        timeZone: dateObjectInput.timeZoneId
      });
    }
    case formats.dateFull: {
      const { value, options } = extractDateInput(input as string | Date | DateObjectInput);
      return intl.formatDate(ensureUTCDate(value as string | Date), { ...configs.dateFull, ...options });
    }
    case formats.dateLong: {
      const { value, options } = extractDateInput(input as string | Date | DateObjectInput);
      return intl.formatDate(ensureUTCDate(value as string | Date), { ...configs.dateLong, ...options });
    }
    case formats.dateMedium: {
      const { value, options } = extractDateInput(input as string | Date | DateObjectInput);
      const dateValue = ensureUTCDate(value as string | Date);
      const overrideLocale = dateMediumLocaleOverrides[intl.locale];
      if (overrideLocale) {
        const defaultTz = intl.timeZone ? { timeZone: intl.timeZone } : {};
        return new Intl.DateTimeFormat(overrideLocale, { ...configs.dateMedium, ...defaultTz, ...options }).format(
          dateValue
        );
      }
      return intl.formatDate(dateValue, { ...configs.dateMedium, ...options });
    }
    case formats.dateShort: {
      const { value, options } = extractDateInput(input as string | Date | DateObjectInput);
      return intl.formatDate(ensureUTCDate(value as string | Date), { ...configs.dateShort, ...options });
    }
    default:
      return intl.formatDate(ensureUTCDate(input as string | Date));
  }
}

export function formatDistance(value: string): string {
  const distance = Number(value);
  const unit = isImperialLocale() ? unitTypes.mile : unitTypes.kilometer;

  return intl.formatNumber(distance, {
    ...configs.unit,
    unit
  });
}

export function formatTime(value: TimeInput | string | Date, format: string): string {
  const partsToTimeStrings = (parts: Intl.DateTimeFormatPart[]) => {
    const time = parts
      .filter(part => ['hour', 'minute', 'second'].includes(part.type))
      .map(part => part.value)
      .join(':');
    const dayPeriodIndex = parts.findIndex(part => part.type === 'dayPeriod');
    return dayPeriodIndex !== -1 ? `${time} ${parts[dayPeriodIndex].value}` : time;
  };
  switch (format) {
    case formats.timeShort: {
      const timeInput = value as TimeInput;
      const timeZoneOptions = timeInput.timeZoneId
        ? { timeZoneName: configs.timeShort.timeZoneName, timeZone: timeInput.timeZoneId }
        : {};
      return partsToTimeStrings(
        intl.formatTimeToParts(ensureUTCDate(timeInput.time as string | Date), timeZoneOptions)
      );
    }
    case formats.timeMedium: {
      return partsToTimeStrings(
        intl.formatTimeToParts(ensureUTCDate((value as TimeInput).time as string | Date), {
          ...configs.timeMedium
        })
      );
    }
    default:
      return partsToTimeStrings(intl.formatTimeToParts(ensureUTCDate(value as string | Date)));
  }
}

export function formatNumber(input: NumberWithConfigInput | string | number): string {
  if (typeof input === 'object') {
    return intl.formatNumber(Number(input.value), {
      ...configs.unit,
      ...input.config
    });
  }

  return intl.formatNumber(Number(input));
}

export function formatOrdinalNumber(value: string | number, t: TFunction): string {
  const ordinalKey = intl.formatPlural(Number(value), {
    type: 'ordinal'
  });
  const getOrdinalNumber = () => {
    switch (ordinalKey) {
      case 'one':
        return t('formats.ordinalNumber.one', {
          value,
          defaultValue: '{{value}}st'
        });
      case 'two':
        return t('formats.ordinalNumber.two', {
          value,
          defaultValue: '{{value}}nd'
        });
      case 'few':
        return t('formats.ordinalNumber.few', {
          value,
          defaultValue: '{{value}}rd'
        });
      case 'other':
        return t('formats.ordinalNumber.other', {
          value,
          defaultValue: '{{value}}th'
        });
      case 'many':
        return t('formats.ordinalNumber.many', {
          value,
          defaultValue: '{{value}}'
        });
      default:
        return value.toString();
    }
  };
  return getOrdinalNumber() || value.toString();
}

export function formatCurrency(value: string | number, currencyCode?: string): string {
  if (currencyCode) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode,
      currencyDisplay: 'symbol'
    }).format(Number(value));
  }
  return intl.formatNumber(Number(value), configs.currency);
}

export function formatRelative(date: string | Date, format: string): string {
  const { value, unit } = selectUnit(ensureUTCDate(date).valueOf());
  switch (format) {
    case formats.relativeNarrow:
      return intl.formatRelativeTime(value, unit, configs.relativeNarrow);
    case formats.relativeShort:
      return intl.formatRelativeTime(value, unit, configs.relativeShort);
    case formats.relativeLong:
    default:
      return intl.formatRelativeTime(value, unit, configs.relativeLong);
  }
}

export function formatDisplayName(
  value: string,
  options: FormatDisplayNameOptions & { customLocale?: string } = { type: displayNameTypes.language }
) {
  if (options.customLocale) {
    const customIntl = createIntl({
      locale: options.customLocale,
      messages: {}
    });

    return customIntl.formatDisplayName(value, options);
  }
  return intl.formatDisplayName(value, options);
}

export function getBrowserLocale() {
  const browserLocale = navigator.languages && navigator.languages.length ? navigator.languages[0] : navigator.language;

  const trimmedLocale = browserLocale.trim();
  const splitLocale = trimmedLocale.split(/-|_/);
  return { language: splitLocale[0], country: splitLocale[1] };
}
